import type { Tasting } from '../types'
import { SANITY_PROJECT_ID, SANITY_DATASET, SANITY_API_VERSION } from '../constants'

const TOKEN_STORAGE_KEY = 'lwwt-sanity-token'
const HOST = `https://${SANITY_PROJECT_ID}.api.sanity.io`
const MUTATE_PATH = `/v${SANITY_API_VERSION}/data/mutate/${SANITY_DATASET}`

export function getSanityToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY)
}

export function setSanityToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token)
}

export async function saveTastingToSanity(tasting: Tasting, token: string): Promise<void> {
  const doc = {
    _id: tasting._id,
    _type: 'tasting' as const,
    theme: tasting.theme,
    host: tasting.host,
    date: tasting.date,
    wines: tasting.wines,
    tasters: tasting.tasters,
    wineAssignments: tasting.wineAssignments,
  }

  const url = new URL(MUTATE_PATH, HOST)
  url.searchParams.set('visibility', 'async')
  url.searchParams.set('returnDocuments', 'false')
  url.searchParams.set('returnIds', 'false')
  url.searchParams.set('tag', 'sanity.lwwt.save-tasting')

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      mutations: [{ createOrReplace: doc }],
    }),
  })

  const isJson = response.headers.get('content-type')?.includes('application/json')

  if (!response.ok) {
    const body = isJson ? await response.json().catch(() => null) : null
    if (isApiError(body)) {
      throw new Error(
        `Failed to save tasting to Sanity: ${body.message} (HTTP ${response.status} ${response.statusText})`,
      )
    }

    throw new Error(`Failed to save tasting to Sanity: ${response.status} ${response.statusText}`)
  }
}

function isApiError(error: unknown): error is { statusCode: number; message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof error.statusCode === 'number' &&
    'message' in error &&
    typeof error.message === 'string'
  )
}
